import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete from "@mui/material/Autocomplete";
import {
  FormControl,
  IconButton,
  MenuItem,
  MenuProps as MenuPropsType,
  Select,
  selectClasses,
  TextField,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import React, { useCallback, useEffect, useState } from "react";
import { FONT_FAMILY, THEME_COLORS } from "../../themes";
import Search from "../Search";

interface SingleSelectV3Props {
  options: any[];
  value: string | number;
  setValue: (val: any) => void;
  placeholder?: string;
  labelExtractor?: string;
  valueExtractor?: string;
  clearIconVisible?: boolean;
  style?: React.CSSProperties;
  noOutline?: boolean;
  disabled?: boolean;
  markup?: boolean;
  containerStyle?: any;
  optionSearchEnabled?: boolean;
  searchEnabled?: boolean;
  removeDefaultMargin?: boolean;
}

const lodash = require("lodash");

function SingleSelectV3({
  placeholder,
  options,
  value,
  setValue,
  labelExtractor,
  valueExtractor,
  clearIconVisible = false,
  noOutline = false,
  disabled = false,
  style,
  markup,
  containerStyle = {},
  optionSearchEnabled = false,
  searchEnabled = false,
  removeDefaultMargin = false,
}: SingleSelectV3Props) {
  const { classes } = useStyles({ removeDefaultMargin });
  const [selectedValue, setSelectedValue] = useState(null);
  let selected = value?.constructor?.name === "Object" ? lodash.get(value, valueExtractor) : value;

  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    setFilteredOptions(options.filter((option) => {
      const label = createOption(labelExtractor ? lodash.get(option, labelExtractor) : option);
      return label?.toLowerCase?.()?.includes?.(searchValue?.toLowerCase?.());
    }));
  }, [searchValue, options]);


  const createOption = useCallback(
    (option) => {
      return markup ? <div dangerouslySetInnerHTML={{ __html: option }} /> : option;
    },
    [markup]
  );

  return !searchEnabled ? (
    <FormControl
      disabled={disabled}
      variant={noOutline ? "standard" : "outlined"}
      margin="dense"
      className={classes.formControl}
      style={containerStyle}
    >
      <Select
        onClose={() => {
          setSearchValue("");
          setFilteredOptions(options);
        }}
        value={selected}
        IconComponent={(props) => <ArrowDropDownIcon style={{ color: THEME_COLORS.purple }} {...props} />}
        onChange={(ev) => {
          setValue(ev.target.value);
        }}
        disabled={disabled}
        displayEmpty
        disableUnderline={noOutline}
        classes={{
          root: noOutline ? classes.noOutlineSelect : classes.select,
          select: classes.selectInput,
        }}
        MenuProps={MenuProps}
        style={style}
      >
        {placeholder && (
          <MenuItem
            classes={{
              root: classes.menuItem,
            }}
            value={""}
            disabled
            style={{ display: "none" }}
          >
            {placeholder}
          </MenuItem>
        )}
        {optionSearchEnabled && (
          <Search customStyle={{ width: "100%", marginBottom: 8 }} placeholder="Search" value={searchValue} onChangeText={(text) => setSearchValue(text)} />
        )}
        {filteredOptions.map((option) => {
          const val = valueExtractor ? lodash.get(option, valueExtractor) : option;

          return (
            <MenuItem
              classes={{
                root: classes.menuItem,
              }}
              key={val}
              value={val}
            >
              {createOption(labelExtractor ? lodash.get(option, labelExtractor) : option)}
              {/* {value == val && <CheckIcon fontSize="small" className={classes.tickMark} />} */}
            </MenuItem>
          );
        })}
      </Select>

      {value !== "" && clearIconVisible && (
        <IconButton size="small" className={classes.clearButton} onClick={() => setValue("")}>
          <ClearIcon fontSize="small" style={{ fontSize: 14 }} />
        </IconButton>
      )}
    </FormControl>
  ) : (
    <Autocomplete
      options={options}
      onChange={(e, v) => setValue(v?.constructor?.name === "Object" ? lodash.get(v, valueExtractor) : v)}
      getOptionLabel={(option) => (labelExtractor ? lodash.get(option, labelExtractor) : option) || ""}
      style={style}
      classes={{
        root: noOutline ? classes.noOutlineSelect : classes.select,
        paper: classes.autoCompletePaper,
        popper: classes.autoCompletePopper,
        input: classes.autocompleteInput,
        inputRoot: classes.autocompleteInput,
      }}
      size="small"
      className={classes.autoCompleteRoot}
      value={selectedValue}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder={placeholder}
            variant={noOutline ? "standard" : "outlined"}
            margin="dense"
            classes={{ root: noOutline ? classes.noOutlineInputRoot : classes.inputRoot }}
          />
        );
      }}
    />
  );
}

const useStyles = makeStyles<any>()((theme, props) => ({
  formControl: {
    margin: 0,
    marginInline: theme.spacing(props.removeDefaultMargin ? 0 : 1),
    marginTop: theme.spacing(props.removeDefaultMargin ? 0 : 2),
    width: 150,
    position: "relative",
    "&:hover": {
      "& > button": {
        visibility: "visible",
      },
    },
  },
  fcRoot: {
    padding: 0,
  },
  select: {
    fontSize: 14,
    padding: "8px 16px",
  },
  noOutlineSelect: {
    fontSize: 14,
    fontFamily: FONT_FAMILY.primaryV3,
    color: THEME_COLORS.purple,
    borderBottom: "0px solid #0000",
    padding: 0,
    "&:focus": {
      background: "inherit",
    },
  },
  menuItem: {
    backgroundColor: THEME_COLORS.sideMenuBg,
    fontSize: 13,
    padding: "0.6em",
    "&:hover": {
      backgroundColor: THEME_COLORS.purple3,
    },
  },
  popover: {
    zIndex: 9999,
  },

  selectInput: { padding: "0 32px 0 0 !important", border: "1px solid black" },

  clearButton: {
    position: "absolute",
    fontSize: 14,
    right: -12,
    top: -12,
    borderRadius: "50%",
    visibility: "visible",
    backgroundColor: THEME_COLORS.purple3,
    "& svg": {
      color: THEME_COLORS.purple,
    },
    "&:hover": {
      backgroundColor: THEME_COLORS.purple,
      "& svg": {
        color: THEME_COLORS.white,
      },
    },
  },
  autoCompleteRoot: {
    marginTop: -4,
    marginLeft: -8,
    marginRight: -8,
    padding: '0px 8px',
  },
  autocompleteInput: {
    fontSize: 14,
    fontFamily: FONT_FAMILY.primaryV3,
    "& > input": {
      padding: "0px 3px",
    },
  },
  autoCompletePaper: {
    fontSize: 13,
    fontFamily: FONT_FAMILY.primaryV3,
    backgroundColor: THEME_COLORS.sideMenuBg,
    maxHeight: 350,
    minWidth: 250,
    overflowY: "auto",
    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        WebkitBoxShadow: "inset 0 0 6px rgba(225,225,225,0.6)",
        backgroundColor: "rgba(128,128,128,0.6)",
      },
      "&::-webkit-scrollbar-track": {
        WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
        backgroundColor: "#F5F5F533",
      },
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 8,
      WebkitBoxShadow: "none",
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "&::-webkit-scrollbar-track": {
      WebkitBoxShadow: "none",
      borderRadius: 10,
      backgroundColor: "transparent",
    },
  },
  autoCompletePopper: {
    fontFamily: FONT_FAMILY.primaryV3,
  },
  noOutlineInputRoot: {
    borderBottom: "0px solid #0000",
  },
  inputRoot: {
    minWidth: 150,
  },
}));

const MenuProps: Partial<MenuPropsType> = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 200,
      backgroundColor: THEME_COLORS.sideMenuBg,
      overflowY: "auto",
    },
  },
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  style: { zIndex: 9999 },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
};

export default SingleSelectV3;
